// Support Framework (touch points) spec §7 — touch-point status display +
// employee-facing wording. Slice 2.
//
// The stored status (`TouchpointStatus`, in ./cases.ts) is the lifecycle state
// the system/manager sets. The *display* status is what HR/Manager/Employee
// surfaces render — for a `Pending` touch point it is DERIVED from how close
// `scheduledFor` is to now (Scheduled → Due soon → Due today → Overdue). This
// module is the single source of truth for that derivation and for the softer
// words the employee sees (§7: "consistent across HR/Manager/Employee views,
// although the wording shown to employees should be softer").

import { OVERDUE_TOUCHPOINT_THRESHOLD_MS, type TouchpointStatus } from './cases.ts';

// The full display-status set (§7 internal statuses). `Not_Started` is for a
// touch point whose date isn't yet known (an RTW-anchored spec before the
// return date is confirmed) — `computeDisplayStatus` never emits it for a live,
// scheduled touch point, but role views use it for the date-unknown case.
export const TOUCHPOINT_DISPLAY_STATUSES = [
  'Not_Started',
  'Scheduled',
  'Due_Soon',
  'Due_Today',
  'Overdue',
  'Completed',
  'Paused',
  'Skipped',
  'HR_Review_Needed',
] as const;
export type TouchpointDisplayStatus = (typeof TOUCHPOINT_DISPLAY_STATUSES)[number];

// The ONLY words an employee is ever shown (§7 employee-facing table). The
// employee view must never surface "overdue", "failed", "non-compliant", etc.
// Every internal display status maps into exactly one of these.
export const EMPLOYEE_FACING_STATUSES = [
  'Upcoming',
  'Coming up',
  'Available now',
  'Completed',
  'Paused for now',
  'Updated',
  'Being reviewed',
] as const;
export type EmployeeFacingStatus = (typeof EMPLOYEE_FACING_STATUSES)[number];

// "Due soon" window — a Pending touch point displays as "Due soon" once it is
// within this many ms of its scheduled date. Engineering interpretation of the
// spec's "approaching" (§7); aligned with the §21 "7 days before" notification
// so the first reminder and the "Due soon" chip appear together. PO-tunable.
export const DUE_SOON_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

// Human labels for the internal (HR/Manager) display status chips (§7 / §8).
export const DISPLAY_STATUS_LABELS: Record<TouchpointDisplayStatus, string> = {
  Not_Started: 'Not started',
  Scheduled: 'Scheduled',
  Due_Soon: 'Due soon',
  Due_Today: 'Due today',
  Overdue: 'Overdue',
  Completed: 'Completed',
  Paused: 'Paused',
  Skipped: 'Skipped',
  HR_Review_Needed: 'HR review needed',
};

// §7 employee-facing wording map. Note the deliberate softening:
//   - Overdue / In_Progress / Due_Today all read as "Available now" — the
//     employee is never told a touch point is late or that they are being
//     chased (§7 / §21 "must avoid making employees feel chased").
//   - Skipped reads as the neutral "Updated", never as a judgement.
const EMPLOYEE_WORDING: Record<TouchpointDisplayStatus, EmployeeFacingStatus> = {
  Not_Started: 'Upcoming',
  Scheduled: 'Upcoming',
  Due_Soon: 'Coming up',
  Due_Today: 'Available now',
  Overdue: 'Available now',
  Completed: 'Completed',
  Paused: 'Paused for now',
  Skipped: 'Updated',
  HR_Review_Needed: 'Being reviewed',
};

// Derive the display status for a touch point from its stored status + how
// close `scheduledFor` is to `now`. Pure — no I/O, deterministic in `now`.
//
// The `Pending` → `Overdue` boundary intentionally matches the legacy
// `isOverdue` rule (`scheduledFor < now - 48h`) so existing overdue behaviour is
// unchanged; the Scheduled/Due_Soon/Due_Today gradations are new.
export function computeDisplayStatus(
  status: TouchpointStatus,
  scheduledFor: Date,
  now: Date,
): TouchpointDisplayStatus {
  switch (status) {
    case 'Completed':
    case 'Done_Informally':
      // Both are "done" from the timeline's point of view.
      return 'Completed';
    case 'Suppressed':
      return 'Paused';
    case 'Skipped':
      return 'Skipped';
    case 'HR_Review_Needed':
      return 'HR_Review_Needed';
    case 'Overdue':
      // Legacy stored value (the repo derives overdue rather than storing it,
      // but keep this total for safety).
      return 'Overdue';
    case 'Pending': {
      const delta = now.getTime() - scheduledFor.getTime();
      if (delta > OVERDUE_TOUCHPOINT_THRESHOLD_MS) return 'Overdue';
      if (delta >= 0) return 'Due_Today';
      if (-delta <= DUE_SOON_WINDOW_MS) return 'Due_Soon';
      return 'Scheduled';
    }
  }
}

// A touch point is "resolved" when no further manager completion is owed and a
// completion must not overwrite it: the manager-set terminal states, plus the
// consent opt-out (`Suppressed`, which a manager must never resurrect). Used by
// the completion idempotency guard (§16). `Adjusted` / `In_Progress` are NOT
// resolved — the touch point is still live.
const RESOLVED_STATUSES = new Set<TouchpointStatus>([
  'Completed',
  'Done_Informally',
  'Skipped',
  'HR_Review_Needed',
  'Suppressed',
]);
export function isResolvedTouchpointStatus(status: TouchpointStatus): boolean {
  return RESOLVED_STATUSES.has(status);
}

// A touch point the employee can no longer set a preference on: it has
// actually finished, one way or another. Narrower than `RESOLVED_STATUSES`
// above — `Suppressed` is itself a preference outcome (un-pausing must stay
// possible) and `HR_Review_Needed` still accepts a preference (it just
// doesn't act on `not_now` by pausing, since HR's review overrides it). Used
// to keep the "Adjust this touch point" control from offering to change
// something that is already done, which previously produced a confusing
// state where a touch point read "Completed" but behaved as paused.
const PREFERENCE_LOCKED_STATUSES = new Set<TouchpointStatus>([
  'Completed',
  'Done_Informally',
  'Skipped',
]);
export function isTouchpointPreferenceLocked(status: TouchpointStatus): boolean {
  return PREFERENCE_LOCKED_STATUSES.has(status);
}

// Map an internal display status to the softer employee-facing word (§7).
export function internalToEmployeeWording(status: TouchpointDisplayStatus): EmployeeFacingStatus {
  return EMPLOYEE_WORDING[status];
}

// Convenience: stored status + dates → employee-facing word in one call.
export function employeeFacingStatus(
  status: TouchpointStatus,
  scheduledFor: Date,
  now: Date,
): EmployeeFacingStatus {
  return internalToEmployeeWording(computeDisplayStatus(status, scheduledFor, now));
}
